// src/components/FilesView.jsx
// View all uploaded files across all deals

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, FileText, Download, Trash2, Search, Filter, 
  ChevronDown, ChevronRight, Folder, LogOut, Loader2, Calendar, User, ExternalLink, X
} from 'lucide-react';
import { supabase, getFileUrl } from '../lib/supabase';
import { useDisplayColors } from '../lib/ThemeContext';

// LFG Brand Colors
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

export default function FilesView({ 
  organizationId,
  onBack,
  onSelectDeal,
  pc = LFG_COLORS.navy,
  sc = LFG_COLORS.gray,
  signOut,
  showToast
}) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const [files, setFiles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDeal, setFilterDeal] = useState('all');
  const [expandedDeals, setExpandedDeals] = useState({});
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'deal'
  const [viewingFile, setViewingFile] = useState(null); // { url, name, type }

  useEffect(() => {
    fetchData();
  }, [organizationId]);

  const fetchData = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    try {
      // Fetch all non-deleted deals
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('id, name, stage')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .order('name');

      if (dealsError) throw dealsError;
      setDeals(dealsData || []);

      // Get deal IDs for filtering files
      const dealIds = (dealsData || []).map(d => d.id);

      // Fetch all files with task and deal info (only from non-deleted deals)
      let filesQuery = supabase
        .from('files')
        .select(`
          id,
          original_name,
          size_bytes,
          storage_path,
          sharepoint_url,
          uploaded_by_id,
          uploaded_by_name,
          uploaded_by_type,
          created_at,
          task_id,
          deal_id,
          organization_id,
          tasks (
            id,
            name,
            deal_id
          )
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      // Only filter by deal_id if there are deals
      if (dealIds.length > 0) {
        filesQuery = filesQuery.in('deal_id', dealIds);
      }

      const { data: filesData, error: filesError } = await filesQuery;

      if (filesError) throw filesError;
      
      // Get deal info for each file
      const orgFiles = (filesData || []).map(f => {
        const deal = dealsData.find(d => d.id === f.deal_id);
        return {
          ...f,
          file_name: f.original_name,
          file_size: f.size_bytes,
          deal_name: deal?.name || 'Unknown Deal',
          deal_stage: deal?.stage
        };
      });
      
      setFiles(orgFiles);
      
      // Start all deals collapsed
      setExpandedDeals({});
      
    } catch (err) {
      console.error('Error fetching files:', err);
      showToast?.('Error loading files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (storagePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('deal-documents')
        .createSignedUrl(storagePath, 60, { download: true });
      
      if (error) throw error;
      
      // Use fetch to get blob and force download
      const response = await fetch(data.signedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      showToast?.('Error downloading file', 'error');
    }
  };

  const handleViewFile = async (file) => {
    try {
      var ext = (file.file_name || '').split('.').pop()?.toLowerCase();
      
      // SharePoint-only files — always open in new tab (SP blocks iframes via CSP)
      if (file.storage_path?.startsWith('sharepoint://')) {
        if (file.sharepoint_url) {
          window.open(file.sharepoint_url, '_blank');
        } else {
          showToast?.('This file is in SharePoint — use SharePoint Files to access it.', 'error');
        }
        return;
      }
      
      // Supabase storage files — open in viewer modal
      const url = await getFileUrl(file.storage_path);
      setViewingFile({ url, name: file.file_name, type: ext });
    } catch (err) {
      console.error('View error:', err);
      showToast?.('Failed to open file', 'error');
    }
  };

  const handleDelete = async (fileId, storagePath, taskId) => {
    if (!confirm('Delete this file? This cannot be undone.')) return;
    
    try {
      // Delete from storage
      await supabase.storage.from('deal-documents').remove([storagePath]);
      
      // Delete from database
      const { error } = await supabase
        .from('files')
        .delete()
        .eq('id', fileId);
      
      if (error) throw error;
      
      setFiles(prev => prev.filter(f => f.id !== fileId));
      showToast?.('File deleted');
    } catch (err) {
      showToast?.('Error deleting file', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Filter and group files
  const filteredFiles = useMemo(() => {
    let result = files;
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(f => 
        f.file_name?.toLowerCase().includes(term) ||
        f.tasks?.name?.toLowerCase().includes(term) ||
        f.deal_name?.toLowerCase().includes(term) ||
        f.uploaded_by_name?.toLowerCase().includes(term)
      );
    }
    
    // Deal filter
    if (filterDeal !== 'all') {
      result = result.filter(f => f.deal_id === filterDeal);
    }
    
    return result;
  }, [files, searchTerm, filterDeal]);

  // Group files by deal
  const filesByDeal = useMemo(() => {
    const grouped = {};
    
    filteredFiles.forEach(file => {
      const dealId = file.deal_id;
      const dealName = file.deal_name || 'Unknown Deal';
      
      if (!grouped[dealId]) {
        grouped[dealId] = {
          dealId,
          dealName,
          dealStage: file.deal_stage,
          files: []
        };
      }
      grouped[dealId].files.push(file);
    });
    
    // Sort deals by name
    return Object.values(grouped).sort((a, b) => 
      a.dealName.localeCompare(b.dealName)
    );
  }, [filteredFiles]);

  const toggleDeal = (dealId) => {
    setExpandedDeals(prev => ({
      ...prev,
      [dealId]: !prev[dealId]
    }));
  };

  const totalFiles = filteredFiles.length;
  const totalSize = filteredFiles.reduce((sum, f) => sum + (f.file_size || 0), 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading files...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Page heading */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Files
          </h1>
          <p className="text-sm mt-1 tabular-nums" style={{ color: '#475569' }}>
            {totalFiles} file{totalFiles === 1 ? '' : 's'} · {formatFileSize(totalSize)} across the portfolio
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div
        className="mb-4 rounded-2xl px-4 py-3 flex gap-3 items-center flex-wrap"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          border: '1px solid #E5E9F0',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
        }}
      >
        <div className="flex-1 relative min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} />
          <input
            type="text"
            placeholder="Search files, tasks, or deals…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full h-9 pl-9 pr-3 text-sm rounded-lg focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: '#94A3B8' }} />
          <select
            value={filterDeal}
            onChange={e => setFilterDeal(e.target.value)}
            className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none min-w-[200px]"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          >
            <option value="all">All Deals</option>
            {deals.map(deal => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* File list */}
      <div>
        {filesByDeal.length === 0 ? (
          <div
            className="rounded-2xl p-10 text-center"
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #E5E9F0',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            }}
          >
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                 style={{ backgroundColor: 'rgba(71, 85, 105, 0.08)' }}>
              <FileText className="w-6 h-6" style={{ color: '#64748B' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>No files found</p>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>
              {searchTerm || filterDeal !== 'all'
                ? 'Try adjusting your search or filter.'
                : 'Files uploaded to deals will appear here.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filesByDeal.map(group => {
              const open = expandedDeals[group.dealId];
              return (
                <div
                  key={group.dealId}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #E5E9F0',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
                  }}
                >
                  {/* Deal header */}
                  <div className="w-full px-5 py-3.5 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => toggleDeal(group.dealId)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left transition-opacity hover:opacity-80"
                    >
                      {open
                        ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                        : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                           style={{ backgroundColor: 'rgba(26, 54, 101, 0.1)', color: '#1A3665' }}>
                        <Folder className="w-3.5 h-3.5" strokeWidth={2.2} />
                      </div>
                      <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{group.dealName}</span>
                      <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: '#F1F5F9', color: '#64748B' }}>
                        {group.files.length} file{group.files.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                    <button
                      onClick={() => onSelectDeal?.(group.dealId)}
                      className="h-8 inline-flex items-center gap-1 px-3 text-[11px] font-medium rounded-lg transition-colors hover:bg-slate-50 flex-shrink-0"
                      style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                    >
                      View Deal →
                    </button>
                  </div>

                  {open && (
                    <div style={{ borderTop: '1px solid #F0F2F6' }}>
                      <div className="grid grid-cols-12 gap-3 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                           style={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F0F2F6', color: '#94A3B8' }}>
                        <div className="col-span-5">File</div>
                        <div className="col-span-2">Task</div>
                        <div className="col-span-2">Uploaded by</div>
                        <div className="col-span-1 text-right">Date</div>
                        <div className="col-span-1 text-right">Size</div>
                        <div className="col-span-1 text-right">Actions</div>
                      </div>
                      {group.files.map((file, idx) => {
                        const ext = (file.file_name || '').split('.').pop()?.toLowerCase();
                        const fileTypeColor = ext === 'pdf' ? '#DC2626'
                          : ['xls', 'xlsx', 'csv'].includes(ext) ? '#059669'
                          : ['doc', 'docx'].includes(ext) ? '#1D4ED8'
                          : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) ? '#7C3AED'
                          : '#64748B';
                        return (
                          <div
                            key={file.id}
                            className="grid grid-cols-12 gap-3 px-5 py-3 items-center transition-colors hover:bg-[#FAFBFD]"
                            style={idx > 0 ? { borderTop: '1px solid #F0F2F6' } : {}}
                          >
                            <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-bold uppercase"
                                   style={{ backgroundColor: `${fileTypeColor}15`, color: fileTypeColor }}>
                                {ext ? ext.slice(0, 4) : <FileText className="w-3.5 h-3.5" />}
                              </div>
                              <button
                                type="button"
                                className="text-sm truncate text-left transition-colors"
                                style={{ color: '#0F172A' }}
                                title={file.file_name}
                                onClick={() => handleViewFile(file)}
                                onMouseEnter={e => { e.currentTarget.style.color = '#1A3665'; }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#0F172A'; }}
                              >
                                {file.file_name}
                              </button>
                            </div>
                            <div className="col-span-2 text-xs truncate" style={{ color: '#64748B' }}>
                              {file.tasks?.name || '—'}
                            </div>
                            <div className="col-span-2 text-xs flex items-center gap-1.5 min-w-0" style={{ color: '#475569' }}>
                              <User className="w-3 h-3 flex-shrink-0" style={{ color: '#94A3B8' }} />
                              <span className="truncate">{file.uploaded_by_name || 'Unknown'}</span>
                              {file.uploaded_by_type === 'seller' && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                                      style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                                  Seller
                                </span>
                              )}
                            </div>
                            <div className="col-span-1 text-[11px] text-right tabular-nums" style={{ color: '#64748B' }}>
                              {formatDate(file.created_at)}
                            </div>
                            <div className="col-span-1 text-[11px] text-right tabular-nums" style={{ color: '#64748B' }}>
                              {formatFileSize(file.file_size)}
                            </div>
                            <div className="col-span-1 flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleDownload(file.storage_path, file.file_name)}
                                className="h-7 w-7 rounded-md flex items-center justify-center transition-colors hover:bg-slate-100"
                                style={{ color: '#475569' }}
                                title="Download"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(file.id, file.storage_path, file.task_id)}
                                className="h-7 w-7 rounded-md flex items-center justify-center transition-colors hover:bg-red-50"
                                style={{ color: '#EF4444' }}
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingFile(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium truncate">{viewingFile.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href={viewingFile.url} download={viewingFile.name} className="px-2 py-1 text-xs border rounded hover:bg-gray-100 flex items-center gap-1 text-gray-600">
                  <Download className="w-3 h-3" /> Download
                </a>
                <a href={viewingFile.url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs border rounded hover:bg-gray-100 flex items-center gap-1 text-gray-600">
                  <ExternalLink className="w-3 h-3" /> New Tab
                </a>
                <button onClick={() => setViewingFile(null)} className="p-1 hover:bg-gray-200 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-100">
              {['pdf'].includes(viewingFile.type) ? (
                <iframe src={viewingFile.url} className="w-full h-full border-0" title={viewingFile.name} />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(viewingFile.type) ? (
                <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                  <img src={viewingFile.url} alt={viewingFile.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                  <FileText className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-lg font-medium mb-1">{viewingFile.name}</p>
                  <p className="text-sm mb-4">This file type can't be previewed in the browser.</p>
                  <div className="flex gap-2">
                    <a href={viewingFile.url} download={viewingFile.name} className="px-4 py-2 text-white rounded text-sm hover:opacity-90" style={{ backgroundColor: displayPrimary }}>
                      <Download className="w-4 h-4 inline mr-1" /> Download
                    </a>
                    <a href={viewingFile.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
                      <ExternalLink className="w-4 h-4 inline mr-1" /> Open in New Tab
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}